/**
 * 阿里云OSS上传工具函数 (ali-oss 6.23.0)
 * @param {File} file - 要上传的文件
 * @param {Object} config - OSS配置对象
 * @param {Function} onProgress - 进度回调函数
 * @returns {Promise} 返回上传结果
 */
export async function uploadToOSS(file, config, onProgress) {
  try {
    console.log('开始上传文件:', file.name);
    console.log('文件大小:', file.size);
    console.log('文件类型:', file.type);

    // 验证文件大小（300MB限制）
    if (file.size > 300 * 1024 * 1024) {
      throw new Error('文件大小不能超过300MB');
    }

    if (file.size <= 0) {
      throw new Error('文件大小不能为0');
    }

    // 验证OSS配置
    if (!config.region || !config.accessKeyId || !config.accessKeySecret || !config.bucket) {
      throw new Error('OSS配置不完整');
    }

    // 动态导入ali-oss库
    const OSS = (await import('ali-oss')).default;
    
    // 处理region格式，确保符合ali-oss规范
    let cleanRegion = config.region;
    if (cleanRegion.startsWith('https://')) {
      cleanRegion = cleanRegion.replace('https://', '');
    }
    if (cleanRegion.startsWith('http://')) {
      cleanRegion = cleanRegion.replace('http://', '');
    }
    if (cleanRegion.startsWith(`${config.bucket}.`)) {
      cleanRegion = cleanRegion.replace(`${config.bucket}.`, '');
    }
    
    // 确保region格式正确 (例如: oss-cn-beijing)
    if (cleanRegion.includes('aliyuncs.com')) {
      cleanRegion = cleanRegion.replace('.aliyuncs.com', '');
    }
    
    console.log('原始region:', config.region);
    console.log('处理后的region:', cleanRegion);
    
    // 创建OSS客户端 (ali-oss 6.x API)
    const client = new OSS({
      region: cleanRegion,
      accessKeyId: config.accessKeyId,
      accessKeySecret: config.accessKeySecret,
      bucket: config.bucket,
      timeout: 300000, // 5分钟超时
      retryMax: 3, // 重试3次
    });

    console.log('OSS客户端配置完成，region:', cleanRegion);

    // 使用真实文件名
    const fileName = file.name;
    console.log('上传文件名:', fileName);

    // 上传文件 (ali-oss 6.x API)
    console.log('开始上传文件到OSS...');
    const uploadResult = await client.put(fileName, file, {
      progress: (p) => {
        const percent = Math.round(p * 100);
        console.log(`上传进度: ${percent}%`);
        if (onProgress) {
          onProgress(percent);
        }
      },
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      }
    });

    console.log('上传结果:', uploadResult);

    // 验证上传是否成功
    if (!uploadResult || !uploadResult.url) {
      throw new Error('文件上传失败，请重试');
    }

    // 构建永久URL
    const encodedFileName = encodeURIComponent(fileName);
    const fileUrl = `https://${config.bucket}.${cleanRegion}.aliyuncs.com/${encodedFileName}`;
    
    console.log('文件URL:', fileUrl);

    return {
      success: true,
      url: fileUrl,
      fileName: fileName,
      fileSize: file.size,
      fileSizeMB: (file.size / (1024 * 1024)).toFixed(2)
    };

  } catch (error) {
    console.error('上传失败:', error);
    return {
      success: false,
      error: error.message
    };
  }
}


/**
 * 获取OSS配置
 * @param {Object} api - API实例对象
 * @returns {Promise<Object>} OSS配置对象
 */
export async function getOSSConfig(api) {
  try {
    const response = await api.aliyun_osssts2();
    return response.data;
  } catch (error) {
    console.error('获取OSS配置失败:', error);
    throw new Error('获取OSS配置失败');
  }
}
